import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

import LegalFooter from './LegalFooter';
import '../legal.css';

export interface TocEntry {
  id: string;
  text: string;
}

interface LegalPageProps {
  title: string;
  html: string;
  toc: TocEntry[];
}

/**
 * Renders a converted legal document (Privacy / Terms). The `html` is generated
 * verbatim from the source .docx by scripts/convert_legal_docs.py — its text is
 * our own trusted, HTML-escaped content, so dangerouslySetInnerHTML is safe here.
 * A sticky table of contents (H2 sections) is shown on wide screens.
 */
const LegalPage: React.FC<LegalPageProps> = ({ title, html, toc }) => {
  useEffect(() => {
    document.title = `${title} · Dial Sniper`;
  }, [title]);

  return (
    <div style={wrapStyle}>
      <div style={containerStyle}>
        <header style={headerStyle}>
          <h1 style={brandStyle}>Dial Sniper</h1>
          <Link to="/" style={backStyle}>← Back to app</Link>
        </header>

        <div style={layoutStyle}>
          {toc.length > 0 && (
            <aside style={tocStyle} className="legal-toc">
              <div style={tocTitleStyle}>On this page</div>
              <nav>
                {toc.map((t) => (
                  <a key={t.id} href={`#${t.id}`} style={tocLinkStyle}>{t.text}</a>
                ))}
              </nav>
            </aside>
          )}

          <article className="legal-doc" dangerouslySetInnerHTML={{ __html: html }} />
        </div>

        <LegalFooter />
      </div>
    </div>
  );
};

const wrapStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: 'var(--bg)',
  padding: '32px 24px 48px',
};
const containerStyle: React.CSSProperties = {
  maxWidth: 1040,
  margin: '0 auto',
};
const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 16,
  marginBottom: 24,
  paddingBottom: 14,
  borderBottom: '2px solid var(--border)',
};
const brandStyle: React.CSSProperties = {
  margin: 0,
  color: 'var(--amber)',
  fontFamily: 'var(--font-display)',
  letterSpacing: '0.06em',
  fontSize: 28,
};
const backStyle: React.CSSProperties = {
  color: 'var(--text-faint)',
  fontSize: 13,
  textDecoration: 'none',
  letterSpacing: '0.04em',
  whiteSpace: 'nowrap',
};
const layoutStyle: React.CSSProperties = {
  display: 'flex',
  gap: 32,
  alignItems: 'flex-start',
};
const tocStyle: React.CSSProperties = {
  position: 'sticky',
  top: 24,
  flex: '0 0 240px',
  maxHeight: 'calc(100vh - 80px)',
  overflowY: 'auto',
};
const tocTitleStyle: React.CSSProperties = {
  color: 'var(--text-soft)',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.14em',
  marginBottom: 10,
};
const tocLinkStyle: React.CSSProperties = {
  display: 'block',
  color: 'var(--text-faint)',
  fontSize: 12,
  textDecoration: 'none',
  padding: '4px 0',
  lineHeight: 1.4,
};

export default LegalPage;
